/**
 * Events - Analytics event tracking utility
 * 
 * Handles tracking clicks on elements with data-event attributes
 * and sending events to Google Analytics
 */

// Define types for event tracking
interface EventData {
  category: string | undefined;
  action: string | undefined;
  label: string | undefined;
}

// Add type definition for gtag function (Google Analytics)
declare global {
  interface Window {
    gtag: (command: string, action: string, params?: Record<string, string | undefined>) => void;
  }
}

export class EventTracker {
  private static instance: EventTracker | null = null;
  private isInitialized = false;
  private triggers: HTMLElement[] = [];

  // Private constructor prevents direct construction calls with 'new'
  private constructor() {}

  // Static method to get the instance (Singleton pattern)
  public static getInstance(): EventTracker {
    if (!EventTracker.instance) {
      EventTracker.instance = new EventTracker();
    }
    return EventTracker.instance;
  }

  /**
   * Initialize the event tracker
   */
  public init(): void {
    if (this.isInitialized) {
      console.warn('EventTracker already initialized');
      return;
    }

    // Get all elements with data-event attribute
    this.triggers = Array.from(document.querySelectorAll('[data-event]'));

    // Add click event listeners to each trigger
    if (this.triggers.length) {
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.handleTrigger(trigger));
      });
    }

    this.isInitialized = true;
  }

  /**
   * Handle a click on a trigger element
   * @param trigger - The element that was clicked
   */
  private handleTrigger(trigger: HTMLElement): void {
    const event: EventData = {
      category: trigger.dataset.cat,
      action: trigger.dataset.action,
      label: trigger.dataset.label,
    };

    // Send the event to Google Analytics if gtag is available
    if (typeof window.gtag === 'function') {
      window.gtag('event', event.action || 'click', {
        event_category: event.category,
        event_label: event.label,
      });
    } else {
      console.warn('Google Analytics gtag function not available');
    }
  }
}

// Export a single instance
export const events = EventTracker.getInstance();

// For backward compatibility with import default
export default EventTracker.getInstance();